import * as Sentry from '@sentry/nextjs'
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { i18nResources } from './i18nResources'

/**
 * Translations are bundled into the JS at build time rather than fetched at runtime (ENG-33377).
 *
 * The previous setup used `i18next-http-backend` to GET `/locales/{{lng}}/{{ns}}.json` on every
 * page load. Those URLs are unhashed and identical across deploys, and the backend does not retry
 * a 4xx or a JSON parse failure, so a namespace could end up empty-but-"ready" and every key in it
 * rendered as its raw key string with no error surfaced anywhere. Bundling makes the copy travel
 * inside content-hashed chunks, so strings and the code referencing them are always the same
 * version, and there is no request left to fail or to serve from a stale cache.
 *
 * `resources` also makes i18next skip its loading path entirely, so it is ready synchronously.
 */

// A missing key renders on every re-render of its component, so report each one once per session
// rather than flooding Sentry with the same event.
const reportedMissingKeys = new Set<string>()

const reportMissingKey = (
  languages: readonly string[],
  namespace: string,
  key: string
) => {
  const fingerprint = `${languages[0]}:${namespace}:${key}`
  if (reportedMissingKeys.has(fingerprint)) return
  reportedMissingKeys.add(fingerprint)

  Sentry.captureMessage(`i18n missing key: ${namespace}:${key}`, {
    level: 'error',
    tags: { feature: 'i18n', action: 'missing_key' },
    // `languages` is the save-*target* list (`saveMissingTo` defaults to `fallback`, so always
    // `en`), not the session's language — read the latter off i18n itself.
    extra: { key, namespace, language: i18n.language },
  })
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: i18nResources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'de'],
    // Strip region codes (e.g. de-DE → de) so browser locales match our supported languages
    load: 'languageOnly',
    // Always derive language from the browser/OS setting — no caching so operators
    // switching machines or changing their browser language get the correct locale
    // immediately without needing to clear localStorage
    detection: {
      order: ['navigator', 'htmlTag'],
      caches: [],
    },
    defaultNS: 'common',
    // Required for `missingKeyHandler` to fire. There is no backend to write to, so this only
    // routes misses to the handler below.
    saveMissing: true,
    missingKeyHandler: (languages, namespace, key) =>
      reportMissingKey(languages, namespace, key),
    interpolation: {
      escapeValue: false,
    },
  })

export { i18n }
